import type { AnalyticsCard, AnalyticsOverview } from '../../services/analytics';
import {
    DASH,
    formatBRL,
    formatInt,
    formatPct,
    formatSignedInt,
    formatSignedPct,
} from './format';

export type OverviewCardId =
    | 'contasTotais'
    | 'contasComAcesso'
    | 'pagantesAtivas'
    | 'mrr'
    | 'arr'
    | 'churn'
    | 'crescimentoPagantes'
    | 'novasContas'
    | 'cancelamentos'
    | 'mrrCancelado';

export interface OverviewCardView {
    id: OverviewCardId;
    title: string;
    value: string;
    caption: string;
    highlight?: boolean;
}

/** "Anterior: 5 · -2 (-40%)". Sem periodo anterior comparavel, so o traco. */
export function comparisonCaption(card: AnalyticsCard): string {
    if (card.previous === null) return `Anterior: ${DASH}`;
    const parts = [`Anterior: ${formatInt(card.previous)}`];
    if (card.change !== null) {
        const pct = card.changePct !== null ? ` (${formatSignedPct(card.changePct)})` : '';
        parts.push(`${formatSignedInt(card.change)}${pct}`);
    }
    return parts.join(' · ');
}

export const STATUS_FILTER_CAPTION = 'Indisponível com filtro de status';

/**
 * "1 de 25 pagantes iniciais", com a contagem que o back-end manda. Com filtro de status
 * a taxa fica indisponivel: quem cancelou sai do status filtrado e a conta nao fecha.
 */
export function churnCaption(cards: AnalyticsOverview['cards'], filtroDeStatus = false): string {
    if (filtroDeStatus) return STATUS_FILTER_CAPTION;
    const { count, base } = cards.churn;
    if (!base) return 'Sem pagantes no início do período';
    return `${formatInt(count)} de ${formatInt(base)} pagantes iniciais`;
}

export const shareCaption = (pct: number | null) => (pct === null ? 'Sem contas no total' : `${formatPct(pct)} do total`);

/** Cards da secao Negocio e growth, na ordem e com os textos do prototipo. */
export function buildOverviewCards(cards: AnalyticsOverview['cards'], filtroDeStatus = false): {
    primary: OverviewCardView[];
    secondary: OverviewCardView[];
} {
    return {
        primary: [
            {
                id: 'contasTotais',
                title: 'Contas totais',
                value: formatInt(cards.contasTotais.value),
                caption: 'Uma conta por cliente',
                highlight: true,
            },
            {
                id: 'contasComAcesso',
                title: 'Contas com acesso',
                value: formatInt(cards.contasComAcesso.value),
                caption: shareCaption(cards.contasComAcesso.pct),
            },
            {
                id: 'pagantesAtivas',
                title: 'Pagantes ativas',
                value: formatInt(cards.pagantesAtivas.value),
                caption: shareCaption(cards.pagantesAtivas.pct),
            },
            {
                id: 'mrr',
                title: 'MRR',
                value: formatBRL(cards.mrr.value),
                caption: 'Receita recorrente mensal',
            },
            {
                id: 'arr',
                title: 'ARR',
                value: formatBRL(cards.arr.value),
                caption: 'Receita recorrente anualizada',
            },
            {
                id: 'churn',
                title: 'Churn',
                value: formatPct(cards.churn.value),
                caption: churnCaption(cards, filtroDeStatus),
            },
        ],
        secondary: [
            {
                id: 'crescimentoPagantes',
                title: 'Crescimento de pagantes',
                value: formatPct(cards.crescimentoPagantes.value),
                caption:
                    cards.crescimentoPagantes.value === null
                        ? 'Sem pagantes no início do período'
                        : `${formatSignedInt(cards.crescimentoPagantes.diff)} contas no período`,
            },
            {
                id: 'novasContas',
                title: 'Novas contas',
                value: formatInt(cards.novasContas.value),
                caption: comparisonCaption(cards.novasContas),
            },
            {
                id: 'cancelamentos',
                title: 'Cancelamentos',
                value: formatInt(cards.cancelamentos.value),
                caption: comparisonCaption(cards.cancelamentos),
            },
            {
                id: 'mrrCancelado',
                title: 'MRR cancelado',
                value: formatBRL(cards.mrrCancelado.value),
                caption: 'Valor mensalizado perdido',
            },
        ],
    };
}
